import { useEffect } from 'react';
import { CheckCircle2, AlertTriangle, Info, AlertCircle, X } from 'lucide-react';

import { cn } from '@/utils/cn';
import { useToast, useStore } from '@/app/store';

/* =====================================================================
   Toast (guía v2 § 2.11) — z-index 500, pos fija bottom-8 right-8.
   El estado vive en el store global; este componente se monta una vez
   en App y consume `toast`.
===================================================================== */

const ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const STYLES = {
  success: 'border-[#00E896]/40 bg-[#00E896]/10 text-[#00E896]',
  error: 'border-[#FF3D5A]/40 bg-[#FF3D5A]/10 text-[#FF3D5A]',
  warning: 'border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]',
  info: 'border-white/20 bg-white/5 text-white',
};

const DURATION_MS = 4000;

export function Toast() {
  const toast = useToast();
  const clearToast = useStore((s) => s.clearToast);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(clearToast, DURATION_MS);
    return () => clearTimeout(t);
  }, [toast, clearToast]);

  if (!toast) return null;
  const Icon = ICONS[toast.type];

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ zIndex: 'var(--z-toast)' }}
      className={cn(
        'fixed bottom-8 right-8 max-w-sm px-4 py-3 rounded-xl border backdrop-blur-md',
        'flex items-center gap-3 shadow-2xl font-body text-sm animate-[slideIn_0.25s_ease]',
        STYLES[toast.type],
      )}
    >
      <Icon size={18} />
      <span className="flex-1 text-white">{toast.message}</span>
      <button onClick={clearToast} aria-label="Cerrar" className="text-white/60 hover:text-white">
        <X size={16} />
      </button>
    </div>
  );
}
